import React, { FC, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { PrimaryRoutes } from '../../../common/enums/internal-route-enums';
import Home from '../Home/Home';
import AIAnalystPage from '../ai-analyst/ai-analyst-page';
import CampaignsPage from '../campaigns-page/campaigns-page';
import ContactsPage from '../contacts-page/contacts-page';
import ContactDetailsPage from '../contact-details-page/contact-details-page';
import DataInsightsPage from '../DataInsights/data-insights-page';
import SegmentBuilderPage from '../SegmentBuilder/segment-builder-page';
import SegmentsPage from '../Segments/segments-page';
import SettingsPage from '../Settings/settings-page';
import { Provider, useDispatch } from 'react-redux';
import store from '../../../redux/store';
import { ReactKit } from '../../../common/providers/ReactKitProvider';
import { AppServiceProvider } from '../../../api/providers/AppServiceProvider';
import { clarity } from 'react-microsoft-clarity';
import { StringHelper } from '../../../common/helpers/string-helper';
import CXInsights from '../CXInsights/cx-insights';
import OpsInsights from '../OpsInsights/ops-insights-page';
import LoginPage from '../Auth/login-page';
import PageLayout from 'framework/page-layout';
import { PagePermissionResponseDto } from '../../../models/auth/pagepermissions';
import { Page } from '../../../common/enums/page-enums';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthApiService } from '../../../api/providers/AppServiceProvider';
import { setTenantLogos, setPermittedPages, clearTenantData } from '../../../features/tenant-slice';

const ProtectedRoute: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (inProgress === "startup") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Component that handles permission loading and navigation
const AuthenticatedApp: FC = () => {
  const { accounts, instance } = useMsal();
  const dispatch = useDispatch();
  const [permittedPages, setPermittedPagesLocal] = useState<string[]>([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [isTokenReady, setIsTokenReady] = useState(false);
  const [isAcquiringToken, setIsAcquiringToken] = useState(true);
  
  const authApiService = useAuthApiService();
  const location = useLocation();
  const navigate = useNavigate();

  // First: Acquire token before doing anything else
  useEffect(() => {
    const acquireToken = async () => {
      if (accounts.length > 0 && instance) {
        setIsAcquiringToken(true);
        try {
          // console.log('🔑 [AUTH FLOW] Acquiring token before any API calls...');
          // console.log('🔑 [AUTH FLOW] Browser:', navigator.userAgent);
          // console.log('🔑 [AUTH FLOW] Accounts found:', accounts.length);
          
          await instance.acquireTokenSilent({
            scopes: ['openid', 'profile'],
            account: accounts[0],
          });
          console.log('✅ [AUTH FLOW] Token acquired successfully - ready for API calls');
          setIsTokenReady(true);
        } catch (error: any) {
          console.log('🔑 [AUTH FLOW] Token acquisition failed:', error);
          
          // Handle InteractionRequiredAuthError by triggering interactive login
          if (error.name === 'InteractionRequiredAuthError' || error.errorCode === 'interaction_required') {
            console.log('🔑 [AUTH FLOW] Interaction required - triggering interactive login');
            try {
              await instance.acquireTokenRedirect({
                scopes: ['openid', 'profile'],
                account: accounts[0],
              });
              // User will be redirected to login and back with fresh token
              return; // Don't set isTokenReady to false, let the redirect happen
            } catch (redirectError) {
              console.log('🔑 [AUTH FLOW] Interactive login failed:', redirectError);
              // If interactive login fails, redirect to login
              console.log('🔑 [AUTH FLOW] Redirecting to login due to interactive login failure');
              window.location.href = '/login';
            }
          } else {
            // For other errors, redirect to login
            console.log('🔑 [AUTH FLOW] Token acquisition failed - redirecting to login');
            window.location.href = '/login';
          }
        } finally {
          setIsAcquiringToken(false);
        }
      } else if (accounts.length === 0 && instance) {
        // No accounts found - redirect to login
        console.log('🔑 [AUTH FLOW] No accounts found - redirecting to login');
        window.location.href = '/login';
      }
    };

    acquireToken();
  }, [accounts, instance]);

  // Handle redirect response from interactive login
  useEffect(() => {
    if (instance) {
      instance.handleRedirectPromise()
        .then((response) => {
          if (response) {
            // console.log('✅ [AUTH FLOW] Interactive login successful:', response);
            // Reset token acquisition state to try again
            setIsTokenReady(false);
            setIsAcquiringToken(true);
          }
        })
        .catch((error) => {
          console.log('❌ [AUTH FLOW] Interactive login failed:', error);
        });
    }
  }, [instance]);

  // Load permissions and handle navigation - ONLY after token is ready
  useEffect(() => {
    if (accounts.length > 0 && isTokenReady && !isAcquiringToken && permittedPages.length === 0 && !isLoadingPermissions && !hasPermissionError && !hasNavigated) {
      // console.log('🔍 [AUTH FLOW] Token ready - starting permission load');
      // console.log('🔍 [AUTH FLOW] Current URL:', window.location.href);
      // console.log('🔍 [AUTH FLOW] Pathname:', location.pathname);
      // console.log('🔍 [AUTH FLOW] Search params:', location.search);
      
      setIsLoadingPermissions(true);
      setHasPermissionError(false);
      
      // No delay needed since token is already ready
      //console.log('🔍 [AUTH FLOW] Making API call to get permissions...');
      authApiService.GetAuthorizedPages()
        .then((response: PagePermissionResponseDto) => {
          console.log('✅ [AUTH FLOW] Permissions loaded successfully:', response);
          if(response!=null){
            const pages = response.permissions?.map((x) => x.resourceName) || [];
            setPermittedPagesLocal(pages);
            
            // Store in Redux
            dispatch(setPermittedPages(pages));
            dispatch(setTenantLogos(response.tenantLogos));
            
           // console.log('✅ [AUTH FLOW] User permissions:', pages);
            //console.log('✅ [AUTH FLOW] Tenant logos:', response.tenantLogos);
            
            // Only navigate if user is on root path (not a direct URL)
            if (location.pathname === '/') {
              console.log('🎯 [AUTH FLOW] User on root path - determining default page');
              
              // Define navigation priority order based on navigation-menu.tsx
              const navigationPriority: Page[] = [
                Page.AIAnalyst,      // AI Analyst - first priority
                Page.Dashboard,      // Dashboard - second priority
                Page.DataInsights,   // Sales Insights - third priority
                Page.CXInsights,     // CX Insights - fourth priority
                Page.Segments,       // Customer Segments - fifth priority
                Page.Contacts,       // Customer Profile - sixth priority
                Page.Campaigns,      // Campaigns - seventh priority
                Page.OpsInsights,    // Ops Insights - eighth priority
                Page.Settings,       // Settings - last priority
              ];
              
              const routeMap: Record<string, PrimaryRoutes> = {
                [Page.Dashboard]: PrimaryRoutes.Home,
                [Page.DataInsights]: PrimaryRoutes.DataInsights,
                [Page.CXInsights]: PrimaryRoutes.CXInsights,
                [Page.OpsInsights]: PrimaryRoutes.OpsInsights,
                [Page.AIAnalyst]: PrimaryRoutes.AIAnalyst,
                [Page.Campaigns]: PrimaryRoutes.Campaigns,
                [Page.Segments]: PrimaryRoutes.Segments,
                [Page.Contacts]: PrimaryRoutes.Contacts,
                [Page.Settings]: PrimaryRoutes.Settings,
              };
              
              // Find first available page based on navigation priority order
              let targetRoute = PrimaryRoutes.Home; // Default fallback
              
              for (const page of navigationPriority) {
                if (pages.includes(page)) {
                  const route = routeMap[page];
                  if (route) {
                    targetRoute = route;
                    console.log(`🎯 [AUTH FLOW] Found page in priority order: ${page} -> ${route}`);
                    break;
                  }
                }
              }
              
             // console.log(`🔄 [AUTH FLOW] Navigating to: ${targetRoute}`);
              setHasNavigated(true);
              navigate(targetRoute, { replace: true });
            } else {
              console.log('🔗 [AUTH FLOW] User on direct URL - checking permissions');
              // User is on a direct URL - check if they have permission for this specific page
              const routeToPageMap: Record<string, string> = {
                '/': Page.Dashboard,
                '/home': Page.Dashboard,
                '/dashboard': Page.Dashboard,
                [PrimaryRoutes.DataInsights]: Page.DataInsights,
                [PrimaryRoutes.CXInsights]: Page.CXInsights,
                [PrimaryRoutes.OpsInsights]: Page.OpsInsights,
                [PrimaryRoutes.AIAnalyst]: Page.AIAnalyst,
                [PrimaryRoutes.Campaigns]: Page.Campaigns,
                [PrimaryRoutes.Segments]: Page.Segments,
                [PrimaryRoutes.Contacts]: Page.Contacts,
                [PrimaryRoutes.Settings]: Page.Settings,
              };
              
              const currentRoute = location.pathname;
              const requiredPage = routeToPageMap[currentRoute];
              
              //console.log(`🔍 [AUTH FLOW] Checking permission for route: ${currentRoute} -> ${requiredPage}`);
             // console.log(`🔍 [AUTH FLOW] User has pages:`, pages);
              
              if (requiredPage && !pages.includes(requiredPage)) {
                console.log(`🚫 [AUTH FLOW] No permission for ${currentRoute} (${requiredPage}), redirecting to first available page`);
                // Redirect to first available page if no permission
                const routeMap: Record<string, PrimaryRoutes> = {
                  [Page.Dashboard]: PrimaryRoutes.Home,
                  [Page.DataInsights]: PrimaryRoutes.DataInsights,
                  [Page.CXInsights]: PrimaryRoutes.CXInsights,
                  [Page.OpsInsights]: PrimaryRoutes.OpsInsights,
                  [Page.AIAnalyst]: PrimaryRoutes.AIAnalyst,
                  [Page.Campaigns]: PrimaryRoutes.Campaigns,
                  [Page.Segments]: PrimaryRoutes.Segments,
                  [Page.Contacts]: PrimaryRoutes.Contacts,
                  [Page.Settings]: PrimaryRoutes.Settings,
                };
                
                let targetRoute = PrimaryRoutes.Home; // Default fallback
                for (const page of pages) {
                  const route = routeMap[page];
                  if (route) {
                    targetRoute = route;
                    console.log(`🎯 [AUTH FLOW] Redirecting to first available: ${page} -> ${route}`);
                    break;
                  }
                }
                
                setHasNavigated(true);
                navigate(targetRoute, { replace: true });
              } else if (requiredPage && pages.includes(requiredPage)) {
                // User has permission for this URL - preserve it
                console.log(`✅ [AUTH FLOW] User has permission for ${currentRoute} (${requiredPage}) - preserving URL`);
                setHasNavigated(true);
              } else {
                // Unknown route - redirect to first available
                console.log(`❓ [AUTH FLOW] Unknown route ${currentRoute}, redirecting to first available page`);
                const routeMap: Record<string, PrimaryRoutes> = {
                  [Page.Dashboard]: PrimaryRoutes.Home,
                  [Page.DataInsights]: PrimaryRoutes.DataInsights,
                  [Page.CXInsights]: PrimaryRoutes.CXInsights,
                  [Page.OpsInsights]: PrimaryRoutes.OpsInsights,
                  [Page.AIAnalyst]: PrimaryRoutes.AIAnalyst,
                  [Page.Campaigns]: PrimaryRoutes.Campaigns,
                  [Page.Segments]: PrimaryRoutes.Segments,
                  [Page.Contacts]: PrimaryRoutes.Contacts,
                  [Page.Settings]: PrimaryRoutes.Settings,
                };
                
                let targetRoute = PrimaryRoutes.Home; // Default fallback
                for (const page of pages) {
                  const route = routeMap[page];
                  if (route) {
                    targetRoute = route;
                    break;
                  }
                }
                
                setHasNavigated(true);
                navigate(targetRoute, { replace: true });
              }
            }
          }
        })
        .catch((error) => {
          console.error('❌ [AUTH FLOW] Failed to load permissions:', error);
          setHasPermissionError(true);
        })
        .finally(() => {
          setIsLoadingPermissions(false);
        });
    } else if (accounts.length > 0 && !isTokenReady && !isAcquiringToken) {
      // Token acquisition failed - let MSAL handle the auth flow
      console.log('🔑 [AUTH FLOW] Token acquisition failed - user should be redirected to login by MSAL');
    }
  }, [accounts, isTokenReady, isAcquiringToken, authApiService, permittedPages.length, isLoadingPermissions, hasPermissionError, hasNavigated, location.pathname, navigate, dispatch]);

  // Reset state when user logs out
  useEffect(() => {
    if (accounts.length === 0) {
      setPermittedPagesLocal([]);
      setIsLoadingPermissions(false);
      setHasPermissionError(false);
      setHasNavigated(false);
      setIsTokenReady(false);
      setIsAcquiringToken(true);
      dispatch(clearTenantData());
    }
  }, [accounts, dispatch]);

  // Show loading while acquiring token
  if (isAcquiringToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-ui text-gray-600">Preparing authentication...</p>
        </div>
      </div>
    );
  }

  // If no accounts and not acquiring token, user should be redirected to login by ProtectedRoute
  if (accounts.length === 0 && !isAcquiringToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-ui text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Don't render any routes until permissions are loaded and user has navigated
  if (isLoadingPermissions || !hasNavigated) {
    return (
      <PageLayout 
        isLoading={false} 
        permittedPages={permittedPages}
        isLoadingPermissions={isLoadingPermissions}
        hasPermissionError={hasPermissionError}
      >
        {/* Show loading while permissions are being loaded */}
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      isLoading={false} 
      permittedPages={permittedPages}
      isLoadingPermissions={isLoadingPermissions}
      hasPermissionError={hasPermissionError}
    >
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/dashboard" element={<Home />} />
        <Route path={PrimaryRoutes.AIAnalyst} element={<AIAnalystPage />} />
        <Route path={PrimaryRoutes.Contacts} element={<ContactsPage />} />
        <Route path={PrimaryRoutes.ContactDetails} element={<ContactDetailsPage />} />
        <Route path={PrimaryRoutes.SegmentBuilder} element={<SegmentBuilderPage />} />
        <Route path={PrimaryRoutes.SegmentBuilderDetails} element={<SegmentBuilderPage />} />
        <Route path={PrimaryRoutes.Segments} element={<SegmentsPage />} />
        <Route path={PrimaryRoutes.Campaigns} element={<CampaignsPage />} />
        <Route path={PrimaryRoutes.CXInsights} element={<CXInsights />} />
        <Route path={PrimaryRoutes.DataInsights} element={<DataInsightsPage />} />
        <Route path={PrimaryRoutes.OpsInsights} element={<OpsInsights />} />
        <Route path={PrimaryRoutes.Settings} element={<SettingsPage />} />
      </Routes>
    </PageLayout>
  );
};

const App: FC = () => {
  const { inProgress } = useMsal();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized) return;
    
    if (process.env.NODE_ENV == 'production' && StringHelper.IsNotNullOrEmpty(process.env.REACT_APP_CLARITY_ID)) {
      clarity.init(process.env.REACT_APP_CLARITY_ID!);
    }
    
    setInitialized(true);
  }, [initialized]); 

  if (inProgress === "startup") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-ui text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <Router>
        <ReactKit>
          <AppServiceProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              
              <Route path="/*" element={
                <ProtectedRoute>
                  <AuthenticatedApp />
                </ProtectedRoute>
              } />
              
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </AppServiceProvider>
        </ReactKit>
      </Router>
    </Provider>
  );
};

export default App;